import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { FaCircleCheck } from "react-icons/fa6"
import { TiWarningOutline } from "react-icons/ti"
import { Box, Dialog, HStack, Portal, VStack } from "@chakra-ui/react"
import { Button, ConfettiBackground, StepItem, Text } from "@/components"
import { BACKGROUND_CHECK_STEPS } from "@/constants"
import { useRespondToOffer } from "@/hooks"

interface Props {
  isAccept: boolean
  isOpen: boolean
  dealId: number
  offerNumber: number
  onClose: () => void
}

const STAGE_CONFIRM = "confirm"
const STAGE_SUCCESS = "success"

export const DecisionOfferModal = ({ isAccept, isOpen, dealId, offerNumber, onClose }: Props) => {
  const navigate = useNavigate()
  const [isLoading, setIsLoading] = useState(false)
  const [stage, setStage] = useState<typeof STAGE_CONFIRM | typeof STAGE_SUCCESS>(STAGE_CONFIRM)
  
  useEffect(() => {
    if (isOpen) {
      setStage(STAGE_CONFIRM)
    }
  }, [isOpen])

  const { mutate: respondToOffer } = useRespondToOffer()

  const handleConfirmClick = async () => {
    setIsLoading(true)
    respondToOffer({ dealId, offerNumber, isAccepted: isAccept })
    setStage(STAGE_SUCCESS)
    setIsLoading(false)
  }

  return (
    <Dialog.Root open={isOpen} onOpenChange={(e) => !e.open && onClose()}>
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            {
              stage === STAGE_CONFIRM ? (
                <VStack p={10} gap={5}>
                  <Box borderRadius={5} bg="yellow.400" p={3}>
                    <TiWarningOutline size={30} color="white" />
                  </Box>
                  <Text fontSize="2xl" fontWeight="bold">Are you sure?</Text>
                  <Text textAlign="center">{`Are you sure you want to ${isAccept ? "accept" : "decline"} this offer? Your equity partner will be notified once you ${isAccept ? "accept" : "decline"}.`}</Text>
                  <HStack mt={5}>
                    <Button
                      variant="solid"
                      bg={isAccept ? "brand.500" : "red.500"}
                      onClick={handleConfirmClick}
                      isLoading={isLoading}
                    >
                      {`Yes, ${isAccept ? "Accept" : "Decline"} Offer`}
                    </Button>
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                  </HStack>
                </VStack>
              ) : (
                <VStack p={10} gap={5} position="relative">
                  {
                    isAccept && (
                      <Box position="absolute" top={0} left={0} w="100%" h="100%" zIndex={0}>
                        <ConfettiBackground duration={3000} initialPieces={100} frozenPieces={30} />
                      </Box>
                    )
                  }
                  <Box borderRadius={5} bg={isAccept ? "green.300" : "red.300"} p={3}>
                    <FaCircleCheck size={30} color="white" />
                  </Box>
                  <Text fontSize="2xl" fontWeight="bold">{isAccept ? "Woo!" : "Oh no!"}</Text>
                  <Text
                    textAlign="center"
                    pl={10}
                    pr={10}
                  >
                    {isAccept ? "Great news! Your offer has been accepted, and your equity partner has been notified." : "We're sorry! Your offer has been successfully declined and your equity partner has been notified."}
                  </Text>
                  <Text textAlign="center" pl={10} pr={10}>{isAccept ? "To finalize the process, please complete the background check form." : "Please feel free to contact your equity partner to discuss additional options."}</Text>
                  <Box w="100%" h="1px" bg="gray.200" />
                  {
                    isAccept && (
                      <VStack align="stretch" mb={5}>
                        {
                          BACKGROUND_CHECK_STEPS.map((step, index) => (
                            <StepItem
                              key={index}
                              step={step}
                              isLast={index === BACKGROUND_CHECK_STEPS.length - 1}
                              isCompleted={index < BACKGROUND_CHECK_STEPS.length - 1}
                              isCurrent={index === BACKGROUND_CHECK_STEPS.length - 1}
                            />
                          ))
                        }
                      </VStack>
                    )
                  }
                  {
                    isAccept ? (
                      <Button variant="solid" onClick={() => navigate("/client/compliance")}>Complete Background Check Form</Button>
                    ) : (
                      <Button variant="outline" onClick={onClose}>Done</Button>
                    )
                  }
                </VStack>
              )
            }
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  )
}